import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { routeLabel } from '@/content/routes';

/**
 * Client-side navigation housekeeping that a real page load would do for free.
 *
 * On every route change (but never on first paint, and never for an in-page
 * hash jump) this:
 *   - scrolls back to the top, as the source design did;
 *   - moves focus to the main landmark, so a keyboard user does not carry on
 *     tabbing from wherever the old page's link happened to be;
 *   - announces the new page politely, because a screen reader gets no load
 *     event to tell it anything changed.
 *
 * The announcement is written straight to the live region rather than held in
 * state: a live region only fires when its text changes in an already-mounted
 * node, and this avoids a re-render whose only job is to update text.
 */
export function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const liveRegion = useRef<HTMLDivElement>(null);
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    // Let in-page anchors do their own thing.
    if (hash) return;

    window.scrollTo({ top: 0, behavior: 'auto' });
    document.getElementById('main')?.focus({ preventScroll: true });

    const label = routeLabel(pathname);
    if (liveRegion.current) {
      liveRegion.current.textContent = label ? `${label} page loaded` : 'Page loaded';
    }
  }, [pathname, hash]);

  return (
    <div aria-live="polite" aria-atomic="true" className="sk-visually-hidden" ref={liveRegion} />
  );
}
